import { QuestionnaireQuestions } from '@getpopsure/qnr-framework';
import { QuoteConfig } from 'components/QuotePage/Quote.models';
import { GeneralQuestion } from 'features/generalQuestionnaire/model';

const getPlanQuoteOptionValue = <Questionnaire>(
  questions: GeneralQuestion,
  questionId: keyof Questionnaire
) => {
  const quoteQuestions = questions.filter((q) => q.type === 'QUOTE');
  let quoteQuestionType;
  const quoteQuestion = quoteQuestions.find((q) => {
    const { props } = q as unknown as { props?: QuoteConfig };

    return props?.quoteOptions?.find(({ optionKey, __component }) => {
      if (optionKey === questionId) {
        // eslint-disable-next-line no-underscore-dangle
        quoteQuestionType = {
          'quote-page.quote-customization-slider': 'slider',
          'quote-page.toggle': 'toggle',
        }?.[__component];
        return true;
      }

      return false;
    });
  });

  if (!quoteQuestion || !quoteQuestionType) {
    return {};
  }

  switch (quoteQuestionType) {
    case 'slider':
      return {
        type: 'Currency',
        key: questionId,
        overridePath: quoteQuestion.id,
      };
    case 'toggle':
      return {
        type: 'Boolean',
        key: questionId,
        overridePath: quoteQuestion.id,
      };
    default:
      return {};
  }
};

export const getReviewValue = <Questionnaire extends QuestionnaireQuestions>(
  questions: GeneralQuestion,
  questionId: keyof Questionnaire
) => {
  const question = questions.find((q) => q.id === questionId);

  if (!question) {
    return getPlanQuoteOptionValue<Questionnaire>(questions, questionId);
  }

  switch (question.type) {
    case 'ADDRESS':
      return {
        type: 'Strings',
        key: 'address',
        keys: ['street', 'houseNumber', 'postcode', 'city'],
      };
    case 'NAME':
      return {
        type: 'Strings',
        key: question.id,
        keys: ['firstName', 'lastName'],
      };
    case 'DATE':
      return {
        type: 'Date',
        format: 'DD MMM YYYY',
        key: question.id,
      };

    case 'CHECKBOX':
      return {
        type: 'Array',
        key: question.id,
        valueMap: Object.entries(question.props.mapValue).reduce(
          (valueMap, [key, value]) => {
            if (typeof value === 'string') {
              return { ...valueMap, [key]: value };
            }
            if (typeof value === 'object' && 'title' in value) {
              return { ...valueMap, [key]: value.title };
            }
            return valueMap;
          },
          {}
        ),
      };

    case 'RADIO':
      return {
        type: 'String',
        key: question.id,
        valueMap: Object.entries(question.props.mapValue).reduce(
          (valueMap, [key, value]) => {
            if (typeof value === 'string') {
              return { ...valueMap, [key]: value };
            }
            if (typeof value === 'object' && 'title' in value) {
              return { ...valueMap, [key]: value.title };
            }
            return valueMap;
          },
          {}
        ),
      };

    case 'BOOLEAN':
      return {
        type: 'Boolean',
        key: question.id,
      };

    case 'COUNTRY_SINGLE':
      return {
        type: 'Strings',
        key: question.id,
        keys: ['name'],
      };

    case 'COUNTRY_MULTI':
      return { type: 'Array', key: question.id, pick: ['name'] };

    case 'QUOTE':
      return {
        type: 'Currency',
        key: questionId,
        nestedKey: 'price',
      };

    default:
      return { type: 'String', key: questionId };
  }
};
